import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UnknownAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import {
  fetchConversationData,
  selectConversationState,
  selectTranslationLanguages,
  collapseChat,
  openChatTranslationMenu,
} from '@rsos/reduxDataStore/src/features/chats/chatsSlice';
import { setConversationLanguage } from '@rsos/reduxDataStore/src/features/chats/chatsSlice';
import { TranslationLanguage } from '@rsos/reduxDataStore/src/features/chats/chatsTypes';
import { selectConversationId as selectConversationsConversationId } from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import { useGetConversationSource } from '@rsos/reduxDataStore/src/features/conversations/utils';
import { useSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { selectConversationId as selectRCSConversationId } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import { useSelectedTextFrom911ConversationID } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import {
  pathwayFromConversationSource,
  type LocationRequestPathway,
} from '../../../gainsightTrackings/locationRequestTrackings';
import { AppState } from '../../../types/AppState';
import { type ResolvedPreconfiguredResponse } from '../../PreconfiguredMessages/AllMessages/utils';
import { useTrackTogglingChatBox } from './chatTracking';
import usePreconfiguredMessageActions, {
  type SelectedPreconfiguredMessage,
} from './usePreconfiguredMessageActions';

export type ChatType =
  | 'agencyShare'
  | 'textFrom911'
  | 'rcs'
  | 'conversations'
  | 'collaborator';

export type ChatContextData = {
  /* Reload conversation info from backend */
  refreshConversationData: () => void;

  isCollapsed: boolean;
  collapse?: (isCollapsed: boolean) => void;

  // Translation features are used by citizen-facing chats:
  translationLanguageList: TranslationLanguage[];
  translationLanguage: string | null | undefined;
  setTranslationLanguage(language: TranslationLanguage | null): void;
  detectedLanguage: string | undefined;
  isTranslatingOutgoingMessages: boolean;
  isTranslationMenuOpen: boolean;
  openTranslationMenu(isTranslationMenuOpen: boolean): void;
  translateOutgoingMessages(isTranslatingOutgoingMessages: boolean): void;

  isAllMessagesVisible: boolean;
  setIsAllMessagesVisible: (value: boolean) => void;
  selectedPreconfiguredMessage: SelectedPreconfiguredMessage;
  setSelectedPreconfiguredMessage: (
    value: SelectedPreconfiguredMessage,
  ) => void;
  onCopyToChat: (msg: ResolvedPreconfiguredResponse) => void;
  onSendMessage: (msg: ResolvedPreconfiguredResponse) => void;

  /**
   * Amplitude pathway name to attribute this chat's location requests to. Lives
   * here so the chat and its All Messages modal cannot disagree, and so the
   * mapping is asserted in one place rather than as a literal in each chat.
   */
  locationRequestPathway: LocationRequestPathway;
};

export const ChatContext = createContext<ChatContextData | undefined>(
  undefined,
);

export const ChatContextProvider: FunctionComponent<
  PropsWithChildren<{
    chatType: ChatType;
  }>
> = ({ chatType, children }) => {
  // It's possible we actually want to define a separate ChatContextProvider for
  // each chatType; you can easily do so by cloning this file and trimming down
  // any switch statements; as long as it still fulfills the ChatContext
  // interface. Using a shared version makes it easier to see what features any
  // given chat is missing, and makes it harder to leave a chat out when adding
  // new features, but it does add significant cyclomatic complexity and reduce
  // readability.

  const dispatch: ThunkDispatch<AppState, unknown, UnknownAction> =
    useDispatch();

  const {
    isAllMessagesVisible,
    setIsAllMessagesVisible,
    selectedPreconfiguredMessage,
    setSelectedPreconfiguredMessage,
    onCopyToChat,
    onSendMessage,
  } = usePreconfiguredMessageActions();

  const callerId = useSelectedCallerID() || '';
  const conversationIds: Record<ChatType, string | undefined> = {
    textFrom911: useSelectedTextFrom911ConversationID(callerId),
    conversations: useSelector(selectConversationsConversationId(callerId)),
    agencyShare: undefined,
    rcs: useSelector(selectRCSConversationId(callerId)),
    collaborator: undefined,
  };
  const conversationId = conversationIds[chatType];

  // 'conversations' covers both TCAPI and TTAdmin, so it resolves from the
  // conversation's source rather than from the chat type. agencyShare and
  // collaborator have no Request Location button and so never report.
  const conversationSource = useGetConversationSource(callerId);
  const locationRequestPathways: Record<ChatType, LocationRequestPathway> = {
    textFrom911: 'TF911',
    conversations: pathwayFromConversationSource(conversationSource),
    rcs: 'RCS',
    agencyShare: 'unknown',
    collaborator: 'unknown',
  };
  const locationRequestPathway = locationRequestPathways[chatType];

  const refreshConversationData = useCallback(() => {
    if (!conversationId) {
      return;
    }
    dispatch(fetchConversationData(conversationId));
  }, [dispatch, conversationId]);

  useEffect(() => {
    refreshConversationData();
  }, [refreshConversationData]);

  const {
    sessionId,
    isCollapsed,
    //isPreconfiguredMessagesListOpen,
    lastCallerLanguage: detectedLanguage,
    isTranslationMenuOpen,
    overrideCallerLanguage: translationLanguage,
    outboundTranslation: isTranslatingOutgoingMessages,
  } = useSelector(selectConversationState(conversationId));

  const trackCollapse = useTrackTogglingChatBox();
  const collapse = useCallback(
    (isCollapsed: boolean) => {
      if (!conversationId) {
        // If translation fails, we don't want to kill the chat, so don't throw
        // eslint-disable-next-line no-console
        console.error('Cannot manipulate UI for conversation without an id');
        return;
      }
      trackCollapse({
        isExpanded: !isCollapsed,
        conversationId,
        sessionId,
        chatType,
      });
      // First close the translation menu, if present
      dispatch(
        openChatTranslationMenu({
          conversationId,
          isTranslationMenuOpen: false,
        }),
      );
      // Then close the whole chat
      return dispatch(collapseChat({ conversationId, isCollapsed }));
    },
    [conversationId, trackCollapse, sessionId, chatType, dispatch],
  );

  const openTranslationMenu = useCallback(
    (isTranslationMenuOpen: boolean) => {
      if (!conversationId) {
        // If translation fails, we don't want to kill the chat, so don't throw
        // eslint-disable-next-line no-console
        console.error('Cannot manipulate UI for conversation without an id');
        return;
      }
      return dispatch(
        openChatTranslationMenu({ conversationId, isTranslationMenuOpen }),
      );
    },
    [dispatch, conversationId],
  );

  const setTranslationLanguage = useCallback(
    (translationLanguage: TranslationLanguage | null) => {
      if (!conversationId) {
        // If translation fails, we don't want to kill the chat, so don't throw
        // eslint-disable-next-line no-console
        console.error('Cannot set language on conversation without an id');
        return;
      }
      dispatch(
        setConversationLanguage({
          conversationId,
          selectedLanguageCode: translationLanguage?.languageCode || null,
        }),
      );
    },
    [dispatch, conversationId],
  );

  const translateOutgoingMessages = useCallback(
    (isTranslatingOutgoingMessages: boolean) => {
      if (!conversationId) {
        // If translation fails, we don't want to kill the chat, so don't throw
        // eslint-disable-next-line no-console
        console.error(
          'Cannot set outgoing translation on conversation without an id',
        );
        return;
      }
      dispatch(
        setConversationLanguage({
          conversationId,
          selectedLanguageCode: translationLanguage,
          outboundTranslation: isTranslatingOutgoingMessages,
        }),
      );
    },
    [dispatch, conversationId, translationLanguage],
  );

  const translationLanguageList: TranslationLanguage[] = useSelector(
    selectTranslationLanguages,
  );

  return (
    <ChatContext.Provider
      value={{
        refreshConversationData,
        isCollapsed,
        collapse,
        isTranslationMenuOpen,
        openTranslationMenu,
        translationLanguage,
        setTranslationLanguage,
        translationLanguageList,
        isTranslatingOutgoingMessages,
        translateOutgoingMessages,
        detectedLanguage,
        isAllMessagesVisible,
        setIsAllMessagesVisible,
        selectedPreconfiguredMessage,
        setSelectedPreconfiguredMessage,
        onCopyToChat,
        onSendMessage,
        locationRequestPathway,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw 'Attempted to render a chat outside a ChatContext.';
  }
  return context;
};
